// Asks every open KaziPeople tab whether it is safe to reload.
//
// A service worker activates for the whole origin, so the tab that clicks
// "Update now" is not necessarily the one with work at risk. Someone can be
// mid-payroll in another window; reloading on this tab's say-so alone would
// take that down with it. This asks all of them and collects the answers.
//
// Pure decision below, transport above it, so the rule can be tested without
// opening browsers.

export const CHANNEL = 'kazipeople-recovery';
/** How long to wait for other tabs. Long enough for a busy main thread,
 *  short enough that a click still feels like it did something. */
export const POLL_TIMEOUT_MS = 600;

export interface TabReply {
  tabId: string;
  unsaved: number;
  pending: number;
  /** What this tab would lose, for naming it to the person clicking. */
  labels: string[];
}

export interface Consensus {
  safe: boolean;
  /** Tabs that answered and are holding work. */
  blockers: TabReply[];
  /** True when at least one tab did not answer in time. */
  incomplete: boolean;
}

/** The rule. Deliberately conservative in BOTH directions that matter:
 *
 *  - any tab holding work blocks the reload, not just this one;
 *  - a tab that does not answer in time blocks it too. Silence is not
 *    consent: a tab busy enough to miss a 600ms message is quite likely the
 *    one doing something expensive, and guessing wrong costs someone's work.
 */
export const decideConsensus = (
  replies: TabReply[], expected: number, elapsedAll: boolean,
): Consensus => {
  const blockers = replies.filter((r) => r.unsaved > 0 || r.pending > 0);
  const incomplete = !elapsedAll || replies.length < expected;
  return { safe: blockers.length === 0 && !incomplete, blockers, incomplete };
};

/** The sentence for a refusal caused by OTHER tabs. The person clicking
 *  cannot see that work, so the message has to point at the window. */
export const consensusReason = (c: Consensus): string | null => {
  if (c.safe) return null;
  if (c.blockers.length > 0) {
    const labels = [...new Set(c.blockers.flatMap((b) => b.labels))].slice(0, 3);
    const what = labels.length ? ` (${labels.join(', ')})` : '';
    return c.blockers.length === 1
      ? `Another KaziPeople window has work in progress${what}. Finish or close it, then update.`
      : `${c.blockers.length} other KaziPeople windows have work in progress${what}. Finish or close them, then update.`;
  }
  return 'Another KaziPeople window did not respond, so it may have unsaved work. Close it, or try again in a moment.';
};
